import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { 
  Clock, 
  Mail, 
  LogOut, 
  RefreshCw,
  ShieldCheck
} from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import logoAsset from "@/assets/logo.png.asset.json";

export const Route = createFileRoute("/aguardando")({
  component: AguardandoRoute,
});

function AguardandoRoute() {
  return <Aguardando />;
}

function Aguardando() {
  const { user, profile, signOut, refreshProfile } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const [lastStatus, setLastStatus] = useState(profile?.status);

  const handleStatusChange = (newStatus: string) => {
    if (newStatus === 'aprovado') {
      toast.success("Acesso liberado!");
      navigate({ to: '/fornecedores', search: {} });
    } else if (newStatus === 'nao_cliente') {
      navigate({ to: '/acesso-negado' });
    } else if (newStatus === 'bloqueado') {
      signOut();
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    const updatedProfile = await refreshProfile();
    setRefreshing(false);
    
    if (updatedProfile) {
      if (updatedProfile.status !== lastStatus) {
        setLastStatus(updatedProfile.status);
        handleStatusChange(updatedProfile.status);
        if (updatedProfile.status === 'pendente') {
           toast("Ainda estamos conferindo. Tente novamente em algumas horas.");
        }
      } else {
        toast("Ainda estamos conferindo. Tente novamente em algumas horas.");
      }
    }
  };

  // Visibility focus check
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleRefresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Interval check
  useEffect(() => {
    const interval = setInterval(handleRefresh, 30000);
    return () => clearInterval(interval);
  }, []);

  // Realtime subscription
  useEffect(() => {
    if (!user?.id) return;
    
    const channel = supabase
      .channel(`profile_changes_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          const newProfile = payload.new as any;
          if (newProfile.status === 'aprovado') {
            toast.success("Acesso liberado! Redirecionando...");
            refreshProfile().then(() => {
              navigate({ to: '/fornecedores', search: {} });
            });
          } else if (newProfile.status !== lastStatus) {
            refreshProfile();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, lastStatus]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F7F9F5]">
      {/* Painel Esquerdo */}
      <div className="flex md:flex-col justify-between p-6 md:p-12 bg-[#0C1A12] text-white w-full md:w-1/3 min-h-[100px] md:min-h-screen">
        <Link to="/">
          <img src={logoAsset.url} alt="Gopage" className="h-10 w-10 md:h-12 md:w-12 rounded-xl" />
        </Link>
        <div className="hidden md:block">
          <h2 className="text-4xl font-bold mb-6 leading-tight">Quase lá.</h2>
          <p className="text-lg opacity-80 leading-relaxed">
            Estamos validando seu cadastro junto à Gopage. É rápido.
          </p>
        </div>
        <div className="hidden md:block text-sm opacity-40">© 2026 Gopage</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-[32px] p-10 shadow-xl border border-border text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-[#C6F764]"></div>
          
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-[#C6F764]/20 rounded-full animate-ping"></div>
              <div className="relative bg-[#F7F9F5] p-6 rounded-full border-4 border-white shadow-sm">
                <Clock className="w-12 h-12 text-[#0C1A12]" />
              </div>
            </div>
          </div>

          <h1 className="text-3xl font-bold mb-4 text-[#0C1A12]">Estamos analisando seu cadastro</h1>
          
          <p className="text-muted-foreground mb-8 leading-relaxed">
            Nosso sistema está verificando seu cadastro junto à Gopage liberar seu acesso. Assim que a validação for concluída, você recebe um e-mail e a lista completa fica disponível.
          </p>

          <div className="bg-[#F7F9F5]/50 rounded-2xl p-6 mb-8 border border-border/50 text-left">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 block">E-mail cadastrado</span>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 truncate">
                <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="font-semibold text-[#0C1A12] truncate">{user?.email}</span>
              </div>
              <button 
                  onClick={signOut}
                  className="text-xs font-bold text-[#0C1A12] hover:underline shrink-0"
              >
                  Trocar
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button 
              onClick={handleRefresh}
              disabled={refreshing}
              variant="onLight"
              className="w-full h-[52px] font-semibold rounded-full shadow-md flex items-center justify-center gap-2"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              Já fui aprovado? Atualizar
            </Button>
            
            <Button 
              onClick={signOut}
              variant="outlineLight"
              className="w-full h-[52px] font-semibold rounded-full transition-all flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </Button>
          </div>

          <p className="mt-8 text-xs text-muted-foreground flex items-center justify-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Segurança Gopage
          </p>
        </div>
      </div>
    </div>
  );
}
